import { Hero } from '@/components/sections/Hero';
import { ServiceGrid } from '@/components/sections/ServiceGrid';
import { GuaranteeStrip } from '@/components/sections/GuaranteeStrip';
import { Reviews } from '@/components/sections/Reviews';
import { AreasServed } from '@/components/sections/AreasServed';
import { CTABand } from '@/components/sections/CTABand';
import { QuoteForm } from '@/components/forms/QuoteForm';
import { Reveal } from '@/components/ui/Reveal';
import { Picture } from '@/components/ui/Picture';
import {
  Seo,
  localBusinessJsonLd,
  organizationJsonLd,
  websiteJsonLd,
  faqJsonLd,
} from '@/lib/seo';
import { company } from '@/data/company';

const HOME_FAQ = [
  {
    question: 'What areas of the UK does Bliss Building Contractors cover?',
    answer:
      'We cover London, Essex and Suffolk, working from our hub in Braintree, Essex. That includes Chelmsford, Colchester, Basildon, Ilford, Tilbury, Loughton, Clacton-on-Sea, Ipswich, Bury St Edmunds and the surrounding towns. If your town is not listed, give us a call - we travel widely.',
  },
  {
    question: 'Do you charge for quotes or site surveys?',
    answer:
      'No. All quotes and site surveys are free and carry no obligation. We provide an honest written quote with no hidden extras, and there are no call-out charges.',
  },
  {
    question: 'Are you fully insured and qualified?',
    answer:
      'Yes. Bliss Building Contractors Ltd is fully insured and our trades team are professionally trained and qualified. All work is guaranteed.',
  },
  {
    question: 'Do you offer 24/7 emergency roof repairs?',
    answer:
      'Yes. Our emergency roofers are on call 24 hours a day, 7 days a week, across London, Essex and Suffolk for storm damage, leaks, missing tiles and chimney emergencies.',
  },
  {
    question: 'How quickly can you start?',
    answer:
      'For emergency roofing we aim to attend the same day. For planned projects we usually complete a free site survey within a few working days and start work as soon as you are ready.',
  },
  {
    question: 'What services do you provide?',
    answer:
      'Roofing services, new roof installations, roof repairs, roofline (fascias, soffits, guttering, loft insulation), damp proofing, driveway services (block paving, resin bound, gravel, tarmac, imprinted concrete), landscaping (patios, decking, fencing, artificial grass, brickwork) and spray foam insulation removal.',
  },
];

export default function Home() {
  return (
    <>
      <Seo
        title={`${company.legalName} | Roofing, Driveways & Landscaping in London, Essex & Suffolk`}
        description={`Local roofing, new roofs, roof repairs, driveways, landscaping, damp proofing and spray foam removal across London, Essex and Suffolk. Free site surveys, no call-out charges, all work guaranteed by ${company.legalName}.`}
        path="/"
        keywords="roofing Essex, roofers Essex, new roofs Essex, roof repairs Essex, emergency roofers London, driveway contractors Essex, block paving Essex, resin bound driveways Essex, landscaping Essex, damp proofing Essex, spray foam removal Essex, building contractors London Essex Suffolk"
        jsonLd={[
          organizationJsonLd(),
          websiteJsonLd(),
          localBusinessJsonLd({ withReviews: true }),
          faqJsonLd(HOME_FAQ),
        ]}
      />

      <Hero
        eyebrow="Local contractors · London · Essex · Suffolk"
        title="Trusted local building contractors across Essex, London and Suffolk."
        subtitle="From small repairs to complete roof replacements, driveways and landscaping - your local contracting specialists. Free quotes, no call-out charges, all work guaranteed."
      />

      <GuaranteeStrip />

      <section className="container-page py-20 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal className="prose-bliss">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">Who we are</p>
            <h2 className="mt-2 text-4xl">Building your dream shouldn’t be stressful.</h2>
            <p>
              At {company.legalName} we transform your design imagination into a stunning,
              long-lasting reality. Our team is professionally trained, fully insured and used
              to running tight, well-organised jobs across {company.serviceCoverage}.
            </p>
            <p>
              We treat every customer with respect and patience, and every project with the
              same level of care, whether you’re worried about a leaky pipe or planning a full
              extension. We don’t say <em>“we can’t”</em>; we tell you what we <em>can</em> do.
            </p>
            <p>Get in touch with the team. The best projects start with a simple conversation.</p>
          </Reveal>

          <Reveal delay={0.1} className="relative">
            <div className="absolute -inset-3 -z-10 rounded-2xl bg-linear-to-br from-gold-300/20 to-transparent blur-2xl" />
            <div className="overflow-hidden rounded-2xl border border-paper-100 bg-paper-50 shadow-card">
              <Picture
                src="/images/about/team.jpg"
                alt="Bliss Building Contractors team on a roofing job in Essex"
                className="h-full w-full object-cover"
                imgClassName="h-full w-full object-cover"
                loading="lazy"
                hideOnError
              />
            </div>
          </Reveal>
        </div>
      </section>

      <ServiceGrid />

      <Reviews />

      <AreasServed />

      <section className="bg-paper-0">
        <div className="container-page grid gap-12 py-20 lg:grid-cols-2 lg:items-start lg:py-24">
          <Reveal className="prose-bliss">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">
              Free, no-obligation quote
            </p>
            <h2 className="mt-2 text-4xl">Tell us about your project</h2>
            <p>
              Send us a few details and we’ll get straight back to you. For urgent
              roofing emergencies, please call <strong>{company.phone}</strong>, 24/7.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <QuoteForm />
          </Reveal>
        </div>
      </section>

      <section className="bg-paper-50" aria-labelledby="home-faq-heading">
        <div className="container-page py-20 sm:py-24">
          <Reveal className="mx-auto mb-10 max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">FAQs</p>
            <h2 id="home-faq-heading" className="mt-2 font-display text-3xl font-semibold leading-tight text-navy-900 sm:text-4xl/10">
              Frequently asked questions
            </h2>
            <p className="mt-3 text-ink-700">
              Quick answers to the questions we hear most often. If yours isn’t covered,
              call <a className="text-gold-700 underline-offset-4 hover:underline" href={`tel:${company.phoneTel}`}>{company.phone}</a>.
            </p>
          </Reveal>

          <ul className="mx-auto max-w-3xl divide-y divide-paper-100 rounded-2xl border border-paper-100 bg-paper-0 shadow-card">
            {HOME_FAQ.map((q, i) => (
              <li key={q.question}>
                <details className="group" {...(i === 0 ? { open: true } : {})}>
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4 px-6 py-5 text-left">
                    <span className="font-semibold text-navy-900">{q.question}</span>
                    <span
                      aria-hidden="true"
                      className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-gold-300 text-gold-600 transition group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <div className="px-6 pb-6 pt-0 text-ink-700">{q.answer}</div>
                </details>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTABand />
    </>
  );
}
